/**
 * In-house product analytics — events go to our own Supabase, no third-party
 * service. Fire-and-forget: tracking must never block or break the app.
 *
 * Usage:  track("workout_completed", { duration_min: 42 })
 *
 * Events land in the `analytics_events` table (see
 * supabase/migration_analytics.sql) and surface in the Admin dashboard
 * (retention, DAU, feature usage).
 */

import { supabase } from "./supabase";

let cachedUserId = null;

async function getUserId() {
  if (cachedUserId) return cachedUserId;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    cachedUserId = session?.user?.id || null;
  } catch {
    cachedUserId = null;
  }
  return cachedUserId;
}

// Reset the cache on auth changes so events attribute to the right user.
try {
  supabase.auth.onAuthStateChange(() => { cachedUserId = null; });
} catch { /* non-fatal */ }

/**
 * Record a product event. Safe to call from anywhere; never throws,
 * silently no-ops when signed out or offline.
 */
export function track(event, props = {}) {
  if (!event) return;
  (async () => {
    const userId = await getUserId();
    if (!userId) return; // only track authenticated activity
    await supabase.from("analytics_events").insert({
      user_id: userId,
      event: String(event).slice(0, 64),
      props,
    });
  })().catch(() => { /* analytics must never surface errors */ });
}

// Session bookkeeping — one app_open per page load.
let sessionTracked = false;
export function trackAppOpen() {
  if (sessionTracked) return;
  sessionTracked = true;
  track("app_open", {
    platform: typeof navigator !== "undefined" && /iPhone|iPad/.test(navigator.userAgent) ? "ios" : "web",
  });
}
